import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const toForm = (l = {}) => ({ ...l, bullets: (l.bullets || []).join("\n"), tags: (l.tags || []).join(", ") });

export const ListingEditor = ({ guideId, listing, onSaved }) => {
  const [f, setF] = useState(toForm(listing));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, price_pdf_aud: Number(f.price_pdf_aud) || 0, price_bundle_aud: Number(f.price_bundle_aud) || 0,
        bullets: f.bullets.split("\n").map((s) => s.trim()).filter(Boolean), tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean) };
      const { data } = await api.put(`/guides/${guideId}/listing`, body);
      onSaved(data);
      toast.success("Store listing saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const field = (k, label, Comp = Input, extra = {}) => (
    <div className="space-y-2">
      <Label htmlFor={`listing-${k}`} className="text-[var(--g-dark,#3B1B48)]">{label}</Label>
      <Comp id={`listing-${k}`} data-testid={`listing-${k}-input`} value={f[k] ?? ""} onChange={set(k)} className="bg-white rounded-none" {...extra} />
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-12" data-testid="listing-editor">
      <div className="space-y-6">
        {field("title", "Product title")}
        {field("subtitle", "Subtitle")}
        {field("short_description", "Short description", Textarea, { rows: 2 })}
        {field("long_description", "Long description (Markdown)", Textarea, { rows: 9 })}
        {field("bullets", "What's inside (one per line)", Textarea, { rows: 6 })}
        {field("buyer_note", "Note to buyers (Markdown, optional)", Textarea, { rows: 4, placeholder: "A personal note shown in the purchase email, the free sample and used as the voice for Instagram captions" })}
        <p data-testid="listing-usage-note" className="text-xs text-[#8C827A] -mt-3">Your bullets and note are used in the store listing, buyer emails, the free sample and the carousel's "Inside the guide" slide. Saving refreshes the carousel and sample, and re-sends the listing to the store if it is published.</p>
        {field("tags", "Tags (comma separated)")}
      </div>
      <div className="space-y-6">
        <div className="bg-[#F4EFE6] border border-[#E2D9CC] p-6 space-y-5">
          <p className="eyebrow">Pricing (AUD)</p>
          {field("price_pdf_aud", "PDF guide", Input, { type: "number" })}
          {field("price_bundle_aud", "Guide + toolkit bundle", Input, { type: "number" })}
        </div>
        <div className="bg-[#F4EFE6] border border-[#E2D9CC] p-6 space-y-5">
          <p className="eyebrow">SEO</p>
          {field("seo_title", "SEO title")}
          {field("seo_description", "Meta description", Textarea, { rows: 3 })}
        </div>
        <Button data-testid="listing-save-button" onClick={save} disabled={busy} className="w-full h-12 rounded-none">{busy ? "Saving…" : "Save listing"}</Button>
      </div>
    </div>
  );
};
